import get from 'lodash/get';
import {getRootStore} from '../../app.bootstrap';
import {getAppTracer} from '../../../app.tracer';
import {RouteName} from '../../router/route-names';
import {RolesConstant as ROLES} from '@techsee/techsee-common/lib/constants/account.constants';
import {resolveUserAndAccount, ResolvedUserData} from './auth-resolve.helper';

declare const ENABLE_QOS: boolean;

const trace = getAppTracer('DashboardResolveHelper');

export function resolveEmbeddedInfo(url: URL): {isMiniDashboard: any; isEmbeddedDashboard: any} {
    const browserUtils = getRootStore().browserUtilsService;

    const isMiniDashboardSessionStorage = browserUtils.getFromSessionStorage('miniDashboard');
    const isEmbeddedDashboardSessionStorage = browserUtils.getFromSessionStorage('embeddedDashboard');

    const isMiniDashboard = isMiniDashboardSessionStorage || url.searchParams.get('mini');
    const isEmbeddedDashboard = isEmbeddedDashboardSessionStorage || url.searchParams.get('mini');

    if (!isMiniDashboardSessionStorage && isMiniDashboard) {
        browserUtils.saveToSessionStorage('miniDashboard', true);
    }

    if (!isEmbeddedDashboardSessionStorage && isEmbeddedDashboard) {
        browserUtils.saveToSessionStorage('embeddedDashboard', true);
    }

    return {isMiniDashboard, isEmbeddedDashboard};
}

/**
 * Resolves currentUser and accountData for dashboard loaders.
 * Logs dashboardModuleLoaded event and redirects to EMB_DASHBOARD_ERROR when embedded.
 */
export async function resolveDashboardUserAndAccount(
    embeddedInfo: {isMiniDashboard: any; isEmbeddedDashboard: any},
    url: URL
): Promise<ResolvedUserData> {
    const rootStore = getRootStore();
    const roomHint = url.searchParams.get('room');

    rootStore.eventLogService.dashboardModuleLoaded({roomHint});

    const failRedirect = embeddedInfo.isEmbeddedDashboard ? RouteName.EMB_DASHBOARD_ERROR : RouteName.LOGIN;

    return resolveUserAndAccount({failRedirect});
}

export async function resolveRoomData(
    currentUser: any,
    accountData: any,
    embeddedInfo: {isMiniDashboard: any; isEmbeddedDashboard: any},
    url: URL
): Promise<any> {
    const rootStore = getRootStore();
    let roomId = url.searchParams.get('room');
    const simpleAPIRoomId = url.searchParams.get('r');
    const simpleAPIMode = simpleAPIRoomId || url.searchParams.get('p') || url.searchParams.get('cid');
    const loadedFromHistory = url.searchParams.get('history');
    const libraryTaggingMode =
        currentUser.role === ROLES.KNOWLEDGE_EXPERT && !accountData.protectedSettings.knowledgeExpertRunSessions;
    const byShortId = !accountData.protectedSettings.showRoomIdInUrl && !loadedFromHistory;

    if (libraryTaggingMode) {
        return {};
    }

    if (loadedFromHistory) {
        try {
            const savedRoomId = await rootStore.tsSessionData.getValue('roomId');

            roomId = savedRoomId;

            const room = await rootStore.roomService.getById(roomId || simpleAPIRoomId || '', byShortId, true);

            rootStore.eventLogService.room = get(room, '_id');

            return room;
        } catch (err: any) {
            if (simpleAPIMode) {
                return err;
            }

            trace.error(
                'Failed to resolve roomData (history): ' +
                    JSON.stringify({
                        isMinidashboard: embeddedInfo.isMiniDashboard,
                        isEmbeddedDashboard: embeddedInfo.isEmbeddedDashboard
                    }),
                err
            );

            await rootStore.tsReactStateHelper.navigateTo(
                embeddedInfo.isEmbeddedDashboard ? RouteName.EMB_DASHBOARD_ERROR : RouteName.INVITE
            );

            return;
        }
    }

    try {
        const room = await rootStore.roomService.getById(roomId || simpleAPIRoomId || '', byShortId);

        rootStore.eventLogService.room = get(room, '_id');

        if (ENABLE_QOS) {
            rootStore.agentQosService.init(accountData, currentUser, room);
        }

        return room;
    } catch (err: any) {
        if (simpleAPIMode) {
            return err;
        }

        trace.error(
            'Failed to resolve roomData: ' +
                JSON.stringify({
                    isMinidashboard: embeddedInfo.isMiniDashboard,
                    isEmbeddedDashboard: embeddedInfo.isEmbeddedDashboard
                }),
            err
        );

        await rootStore.tsReactStateHelper.navigateTo(
            embeddedInfo.isEmbeddedDashboard ? RouteName.EMB_DASHBOARD_ERROR : RouteName.INVITE
        );

        return;
    }
}
